((window, document, Bootstrap) => {
	const themeAssets = window.APE_THEME_ASSETS || {};
	const scriptLoadPromises = new Map();

	const loadScript = (name, src) => {
		if (!src) {
			return Promise.resolve(false);
		}

		const existingPromise = scriptLoadPromises.get(name);
		if (existingPromise) {
			return existingPromise;
		}

		const promise = new Promise((resolve) => {
			const existingByData = document.querySelector(
				`script[data-lazy-script="${name}"]`,
			);
			const existingBySrc = Array.from(document.scripts).find(
				(script) => script.src === src,
			);
			const existingScript = existingByData || existingBySrc;

			if (existingScript) {
				if (
					existingScript.dataset.loaded === "true" ||
					existingScript.readyState === "complete"
				) {
					resolve(true);
					return;
				}

				existingScript.addEventListener("load", () => resolve(true), {
					once: true,
				});
				existingScript.addEventListener("error", () => resolve(false), {
					once: true,
				});
				return;
			}

			const script = document.createElement("script");
			script.src = src;
			script.async = true;
			script.defer = true;
			script.dataset.lazyScript = name;
			script.addEventListener(
				"load",
				() => {
					script.dataset.loaded = "true";
					resolve(true);
				},
				{ once: true },
			);
			script.addEventListener("error", () => resolve(false), { once: true });
			document.head.appendChild(script);
		});

		scriptLoadPromises.set(name, promise);
		return promise;
	};

	const ensureInputmask = async () => {
		if (typeof window.Inputmask === "function") {
			return true;
		}
		return loadScript("inputmask", themeAssets.inputmask);
	};

	const ensureSwiper = async () => {
		if (typeof window.Swiper === "function") {
			return true;
		}
		return loadScript("swiper", themeAssets.swiper);
	};

	window.addEventListener("DOMContentLoaded", () => {
		contact();
		showElementsWhenOnViewport();
		sectionQuickNav();
		backToTop();
		void masks();
		void carousel();
	});

	const masks = async () => {
		const hasMaskField = document.querySelector(
			"input[data-inputmask], input.mask-cellphone",
		);
		if (!hasMaskField) {
			return;
		}

		const hasInputmask = await ensureInputmask();
		if (!hasInputmask || typeof window.Inputmask !== "function") {
			return;
		}

		const Inputmask = window.Inputmask;
		Inputmask().mask(document.querySelectorAll("input[data-inputmask]"));
		try {
			const im = new Inputmask("(99) 99999-9999");
			im.mask(document.querySelector("input.mask-cellphone"));
		} catch {}
	};

	const contact = () => {
		try {
			const modalElement = document.querySelector("#contato");
			if (modalElement && Bootstrap?.Modal) {
				const modal = Bootstrap.Modal.getOrCreateInstance(modalElement);

				modalElement.addEventListener("click", (event) => {
					const clickedBackdrop = event.target === modalElement;
					const clickedOutsideForm =
						event.target instanceof Element &&
						event.target.closest(".modal-body") &&
						!event.target.closest("form.wpcf7-form");

					if (clickedBackdrop || clickedOutsideForm) {
						modal.hide();
					}
				});
			}
		} catch {}

		try {
			const wpcf7Elm = document.querySelector(".wpcf7");
			const form = document.querySelector("form.wpcf7-form");
			if (!wpcf7Elm || !form) {
				return;
			}

			const btnSubmit = form.querySelector('button[type="submit"]');
			const fields = Array.from(
				form.querySelectorAll("input, textarea, select"),
			).filter(
				(field) =>
					field instanceof HTMLInputElement ||
					field instanceof HTMLTextAreaElement ||
					field instanceof HTMLSelectElement,
			);

			const isValidatableField = (field) => {
				if (
					!(
						field instanceof HTMLInputElement ||
						field instanceof HTMLTextAreaElement ||
						field instanceof HTMLSelectElement
					)
				) {
					return false;
				}

				if (
					field.disabled ||
					field.type === "hidden" ||
					field.type === "submit" ||
					field.type === "button" ||
					field.type === "reset"
				) {
					return false;
				}

				return true;
			};

			const getFieldValue = (field) => field.value.trim();

			const validateField = (field) => {
				if (!isValidatableField(field)) {
					return true;
				}

				const isRequired =
					field.required || field.getAttribute("aria-required") === "true";
				const value = getFieldValue(field);
				let isValid = true;

				if (isRequired && value.length === 0) {
					isValid = false;
				} else if (value.length > 0 || isRequired) {
					isValid = field.checkValidity();
				}

				field.classList.toggle("is-valid", isValid);
				field.classList.toggle("is-invalid", !isValid);
				field.setAttribute("aria-invalid", isValid ? "false" : "true");

				return isValid;
			};

			const validateAllFields = () =>
				fields
					.filter((field) => isValidatableField(field))
					.every((field) => validateField(field));

			const handleRealtimeValidation = (event) => {
				validateField(event.target);
			};

			form.addEventListener("input", handleRealtimeValidation);
			form.addEventListener("change", handleRealtimeValidation);
			form.addEventListener("focusout", handleRealtimeValidation);

			form.addEventListener("submit", (event) => {
				const formIsValid = validateAllFields();
				if (!formIsValid) {
					event.preventDefault();
					event.stopPropagation();
					form.classList.add("was-validated");
					form.classList.remove("needs-validation");
					return;
				}

				if (btnSubmit) {
					btnSubmit.setAttribute("disabled", "disabled");
					btnSubmit.innerHTML = "ENVIANDO...";
				}
			});
			wpcf7Elm.addEventListener(
				"wpcf7submit",
				(event) => {
					if (btnSubmit) {
						btnSubmit.removeAttribute("disabled");
						btnSubmit.innerHTML = "QUERO MEU DIAGNÓSTICO GRATUITO";
					}
					event.target.classList.add("was-validated");
					event.target.classList.remove("needs-validation");
					const invalidFields = event.detail?.apiResponse?.invalid_fields ?? [];
					const invalidNames = new Set(
						invalidFields
							.map((item) => item?.field)
							.filter((name) => typeof name === "string" && name.length > 0),
					);
					fields.forEach((field) => {
						if (!isValidatableField(field)) {
							return;
						}

						if (invalidNames.has(field.name)) {
							field.classList.remove("is-valid");
							field.classList.add("is-invalid");
							field.setAttribute("aria-invalid", "true");
							return;
						}

						if (getFieldValue(field).length > 0) {
							field.classList.remove("is-invalid");
							field.classList.add("is-valid");
							field.setAttribute("aria-invalid", "false");
						}
					});
					if (event.detail?.apiResponse?.status === "mail_sent") {
						event.target.classList.remove("was-validated");
						event.target.classList.add("needs-validation");
						fields.forEach((field) => {
							field.classList.remove("is-valid", "is-invalid");
							field.setAttribute("aria-invalid", "false");
						});
					}
				},
				false,
			);
			form.querySelectorAll("[aria-required='true']").forEach((input) => {
				input.setAttribute("required", "required");
			});
		} catch {}
	};

	const showElementsWhenOnViewport = () => {
		const cleanupButtonAnimationClass = (element, animationClass) => {
			if (
				!(element instanceof HTMLElement) ||
				!element.classList.contains("btn")
			) {
				return;
			}

			let hasCleanedUp = false;
			const finishAnimation = () => {
				if (hasCleanedUp) {
					return;
				}

				hasCleanedUp = true;
				element.classList.remove(animationClass);
				element.classList.remove("animate__animated");
				element.style.opacity = "1";
			};

			const fallbackTimer = window.setTimeout(finishAnimation, 1800);
			element.addEventListener(
				"animationend",
				() => {
					window.clearTimeout(fallbackTimer);
					finishAnimation();
				},
				{ once: true },
			);
		};

		const elementsMap = {
			"#strategy .section-title": "animate__fadeInDown",
			"#strategy .section-title + p": "animate__fadeInDown",
			"#strategy ul li": "animate__fadeInLeft",
			"#strategy blockquote": "animate__fadeInDown",
			"#organize .section-title": "animate__fadeInLeft",
			"#organize .btn": "animate__fadeInLeft",
			"#organize h3": "animate__fadeInRight",
			"#organize ul li": "animate__fadeInRight",
			"#organize p": "animate__fadeInDown",
			"#diagnosis .section-title": "animate__fadeInDown",
			"#diagnosis ul li": "animate__fadeInLeft",
			"#diagnosis .btn": "animate__fadeInUp",
			"#nate img": "animate__fadeInUp",
			"#nate .section-title": "animate__fadeInDown",
			"#nate p": "animate__fadeInUp",
			"#not-included .section-title": "animate__fadeInLeft",
			"#not-included p": "animate__fadeInLeft",
			"#not-included ul li": "animate__fadeInRight",
			"#not-included blockquote": "animate__fadeInDown",
			"#testimonials-and-next-steps .testimonials .section-title":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .testimonials p": "animate__fadeInDown",
			"#testimonials-and-next-steps .testimonials .swiper":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .col .section-title":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps p": "animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps ul.glass":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .next-steps__content .section-title":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .next-steps__content ul:not(.next-steps__deliverables) li":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .next-steps__content ul":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .next-steps__content blockquote":
				"animate__fadeInDown",
			"#testimonials-and-next-steps .next-steps .next-steps__content .btn":
				"animate__fadeInDown",
		};

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const animationClass = entry.target.dataset.animation;
						if (animationClass) {
							entry.target.classList.add(animationClass);
							cleanupButtonAnimationClass(entry.target, animationClass);
						}
						observer.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: "0px 0px -100px 0px" },
		);

		Object.entries(elementsMap).forEach(([selector, className]) => {
			const elements = document.querySelectorAll(selector);
			elements.forEach((element) => {
				element.dataset.animation = className;
				observer.observe(element);
			});
		});
	};

	const carousel = async () => {
		const hasCarousel = document.querySelector(".swiper");
		if (!hasCarousel) {
			return;
		}

		const hasSwiper = await ensureSwiper();
		if (!hasSwiper || typeof window.Swiper !== "function") {
			return;
		}

		const Swiper = window.Swiper;
		new Swiper(".swiper", {
			autoplay: {
				delay: 3000,
				disableOnInteraction: true,
				pauseOnMouseEnter: true,
			},
			slidesPerView: 1,
			centeredSlides: false,
			spaceBetween: 16,
			autoHeight: true,
			loop: false,
			pagination: {
				el: ".swiper-pagination",
				clickable: true,
				renderBullet: (index, className) =>
					`<button type="button" class="${className} swiper-pagination-bullet">${index + 1}</button>`,
			},
			navigation: false,
			breakpoints: {
				320: {
					slidesPerView: 1,
				},
				480: {
					slidesPerView: 2,
				},
				800: {
					slidesPerView: 3,
				},
			},
		});
	};

	const sectionQuickNav = () => {
		const nav = document.querySelector(".section-quick-nav");
		const toggle = nav?.querySelector(".section-quick-nav__toggle");
		const list = nav?.querySelector(".section-quick-nav__list");
		const body = document.body;
		const links = nav
			? Array.from(nav.querySelectorAll(".section-quick-nav__link"))
			: [];

		if (!nav || !toggle || !list || !body || links.length === 0) {
			return;
		}

		const setOpen = (isOpen) => {
			toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
			list.hidden = !isOpen;
		};

		const focusWithoutScroll = (element) => {
			if (!(element instanceof HTMLElement)) {
				return;
			}

			if (!element.hasAttribute("tabindex")) {
				element.setAttribute("tabindex", "-1");
			}

			try {
				element.focus({ preventScroll: true });
			} catch {
				element.focus();
			}
		};

		setOpen(false);
		let hasKeyboardSession = false;

		const isDocumentLevelFocus = () => {
			const activeElement = document.activeElement;
			return (
				!activeElement ||
				activeElement === document.body ||
				activeElement === document.documentElement
			);
		};

		const enableKeyboardMode = (event) => {
			if (event.key !== "Tab") {
				return;
			}

			body.classList.add("is-keyboard-navigation");

			if (!hasKeyboardSession && isDocumentLevelFocus()) {
				event.preventDefault();
				focusWithoutScroll(toggle);
			}

			hasKeyboardSession = true;
		};

		const disableKeyboardMode = (event) => {
			if (event.target instanceof Node && nav.contains(event.target)) {
				return;
			}

			body.classList.remove("is-keyboard-navigation");
			setOpen(false);
			hasKeyboardSession = false;
		};

		document.addEventListener("keydown", enableKeyboardMode);
		document.addEventListener("mousedown", disableKeyboardMode, {
			passive: true,
		});
		document.addEventListener("touchstart", disableKeyboardMode, {
			passive: true,
		});

		toggle.addEventListener("click", () => {
			const isExpanded = toggle.getAttribute("aria-expanded") === "true";
			setOpen(!isExpanded);
		});

		document.addEventListener("click", (event) => {
			if (!(event.target instanceof Node)) {
				return;
			}

			if (!nav.contains(event.target)) {
				setOpen(false);
			}
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				setOpen(false);
				focusWithoutScroll(toggle);
			}
		});

		links.forEach((link) => {
			link.addEventListener("click", (event) => {
				const href = link.getAttribute("href") ?? "";
				if (!href.startsWith("#")) {
					return;
				}

				const section = document.querySelector(href);
				if (!(section instanceof HTMLElement)) {
					return;
				}

				event.preventDefault();

				const focusTargetId = link.dataset.focusTarget ?? "";
				const focusTarget = focusTargetId
					? document.getElementById(focusTargetId)
					: null;
				const targetElement =
					focusTarget instanceof HTMLElement ? focusTarget : section;

				targetElement.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});

				window.setTimeout(() => {
					focusWithoutScroll(targetElement);
				}, 350);

				setOpen(false);
			});
		});
	};

	const backToTop = () => {
		const button = document.querySelector("#backToTop");

		if (!button) {
			return;
		}

		const scrollElement = document.scrollingElement || document.documentElement;
		const mainElement = document.querySelector(".site-main");
		const visibilityOffset = 300;

		const getScrollTop = () =>
			Math.max(
				window.scrollY || 0,
				window.pageYOffset || 0,
				scrollElement?.scrollTop || 0,
				document.documentElement?.scrollTop || 0,
				document.body?.scrollTop || 0,
			);

		const updateVisibility = () => {
			if (getScrollTop() > visibilityOffset) {
				button.classList.add("is-visible");
			} else {
				button.classList.remove("is-visible");
			}
		};

		button.addEventListener("click", () => {
			const scrollCandidates = [
				mainElement,
				scrollElement,
				document.body,
				document.documentElement,
			].filter(Boolean);

			const activeScrollTarget = scrollCandidates.reduce((active, current) => {
				const activeTop = active?.scrollTop || 0;
				const currentTop = current?.scrollTop || 0;
				return currentTop > activeTop ? current : active;
			}, scrollCandidates[0]);

			if (
				activeScrollTarget &&
				typeof activeScrollTarget.scrollTo === "function"
			) {
				activeScrollTarget.scrollTo({
					top: 0,
					behavior: "smooth",
				});
			}

			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		});

		const scrollTargets = new Set([
			window,
			document,
			document.documentElement,
			document.body,
			scrollElement,
			mainElement,
		]);

		scrollTargets.forEach((target) => {
			if (target?.addEventListener) {
				target.addEventListener("scroll", updateVisibility, { passive: true });
			}
		});

		updateVisibility();
	};
})(window, window.document, window.bootstrap);
